import { Link } from 'react-router-dom';
import Icon from '../ui/Icon';
import FieldError from '../ui/FieldError';
import { copyText } from '../../utils/clipboard';
import { groupOptions, optionName } from '../stations/stationAdapter';
import { formatAmsterdam } from './amsterdamTime';
import { formatAmount, formatNumber, formatPrice, formatVolume } from './scanAdapter';
import { changedFields, differsFromOcr, fieldId, mathHint, normalizeReceiptNumber, ocrFormValue } from './scanRules';

const inputBase = 'w-full h-11 px-3 rounded-lg text-sm text-on-surface border outline-none transition font-data-mono';
const inputOk = 'bg-surface-container-low border-transparent focus:bg-surface-container-lowest focus:border-primary-container focus:shadow-[0_0_0_3px_rgba(34,197,94,0.15)]';
const inputInvalid = 'bg-status-alert-bg/40 border-error focus:shadow-[0_0_0_3px_rgba(186,26,26,0.15)]';

function Label({ htmlFor, children, extra }) {
  return (
    <div className="flex items-baseline justify-between gap-2 mb-1.5">
      <label htmlFor={htmlFor} className="text-xs font-semibold text-on-surface">
        {children}
      </label>
      {extra}
    </div>
  );
}

/** "OCR: € 2,439 · Use OCR value" under a field whose value differs from what OCR read. */
function OcrHint({ show, text, onUse, editable }) {
  if (!show) return null;
  return (
    <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-on-surface-variant">
      <span>
        OCR: <span className="font-data-mono">{text}</span>
      </span>
      {editable && (
        <button type="button" onClick={onUse} className="font-semibold text-secondary hover:underline rounded outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot">
          Use OCR value
        </button>
      )}
    </p>
  );
}

function ReadValue({ id, children }) {
  return (
    <p id={id} className="min-h-11 px-3 py-2.5 rounded-lg bg-surface-container-low text-sm font-data-mono text-on-surface">
      {children}
    </p>
  );
}

/**
 * "Extracted Transaction Parameters": station (read-only), fuel, price, volume, total,
 * receipt time, ticket number and pump. Inputs only while the scan is pending.
 */
export default function ScanFields({ scan, form, errors, editable, fuelOptions, onChange }) {
  const unit = scan.fuelOption?.unit || 'L';
  const groups = groupOptions(fuelOptions ?? []);
  const activeIds = new Set(groups.flatMap((g) => g.options.map((o) => String(o.id))));
  const currentInactive = form.fuelOptionId && !activeIds.has(form.fuelOptionId) ? scan.fuelOption : null;

  const input = (field, extra = '') => `${inputBase} ${errors[field] ? inputInvalid : inputOk} ${extra}`;
  const describedBy = (field) => (errors[field] ? `${fieldId(field)}-error` : undefined);
  const common = (field) => ({
    id: fieldId(field),
    value: form[field],
    onChange: (e) => onChange(field, e.target.value),
    'aria-invalid': Boolean(errors[field]),
    'aria-describedby': describedBy(field),
  });
  const ocrHint = (field, text) => (
    <OcrHint show={differsFromOcr(form, scan, field)} text={text} editable={editable} onUse={() => onChange(field, ocrFormValue(scan, field))} />
  );

  // Saved values: trust the API's mathCheck; edited values: compute it here
  const mathFieldsEdited = ['unitPrice', 'volume', 'totalAmount'].some((f) => f in changedFields(form, scan));
  const hint = mathHint(form);
  const matches = hint && (!mathFieldsEdited && scan.mathCheck ? scan.mathCheck.matches : hint.matches);
  const mathText = hint && (
    <span
      className={`font-data-mono text-[11px] font-semibold ${matches ? 'text-secondary' : 'text-error'}`}
      data-testid="math-hint"
      aria-live="polite"
    >
      {formatAmount(hint.total)} ÷ {formatNumber(hint.volume, 2)} = {formatPrice(hint.expectedUnitPrice)}
      {matches ? ' ✓' : ` ≠ ${formatPrice(hint.price)}`}
    </span>
  );

  const station = scan.station;
  const stationLine = [station?.name, station?.address].filter(Boolean).join(', ');
  const receiptPostcode = scan.ocr.checks?.receiptPostalCode;
  const age = scan.receiptAgeDays;

  return (
    <section aria-labelledby="scan-params-title" className="bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs p-5 flex flex-col gap-5">
      <h2 id="scan-params-title" className="flex items-center gap-2 text-lg font-bold text-on-surface">
        <Icon name="edit_note" size={22} className="text-primary" />
        Extracted Transaction Parameters
      </h2>

      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1.5">
          <span className="text-xs font-semibold text-on-surface">Gas Station &amp; Location</span>
          {scan.placeId && (
            <span className="flex items-center gap-1 text-[11px] text-on-surface-variant">
              PlaceId: <span className="font-data-mono break-all">{scan.placeId}</span>
              <button
                type="button"
                onClick={() => copyText(scan.placeId, 'Place ID copied')}
                aria-label="Copy place ID"
                className="p-0.5 rounded hover:text-primary outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
              >
                <Icon name="content_copy" size={14} />
              </button>
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-surface-container-low">
          <span className="flex items-center gap-2 text-sm text-on-surface min-w-0">
            <Icon name="storefront" size={20} className="text-primary shrink-0" />
            <span className="truncate">{stationLine || scan.ocr.stationText?.split('\n')[0] || 'Station details unavailable'}</span>
          </span>
          {scan.placeId && (
            <Link
              to={`/gas-stations?search=${encodeURIComponent(scan.placeId)}`}
              className="text-xs font-semibold text-secondary hover:underline whitespace-nowrap rounded outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
            >
              View station
            </Link>
          )}
        </div>
        {scan.stationMatch === false && (
          <p className="mt-1.5 flex items-start gap-1.5 text-xs text-status-pending-text">
            <Icon name="warning" size={16} className="shrink-0" />
            Receipt postcode {receiptPostcode ?? '—'} doesn&apos;t match this station ({station?.postalCode ?? '—'}).
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-5">
        <div>
          <Label htmlFor={fieldId('fuelOptionId')}>Fuel Option</Label>
          {editable ? (
            <div className="relative">
              <select {...common('fuelOptionId')} className={input('fuelOptionId', 'appearance-none pr-9 font-sans')}>
                <option value="">Choose fuel…</option>
                {currentInactive && <option value={String(currentInactive.id)}>{currentInactive.name} (inactive)</option>}
                {groups.map((g) => (
                  <optgroup key={g.key} label={g.label}>
                    {g.options.map((o) => (
                      <option key={o.id} value={String(o.id)}>
                        {optionName(o)} - {o.code}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <Icon name="expand_more" size={20} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-on-surface-variant" />
            </div>
          ) : (
            <ReadValue id={fieldId('fuelOptionId')}>{scan.fuelOption ? `${scan.fuelOption.name} - ${scan.fuelOption.code}` : '—'}</ReadValue>
          )}
          <FieldError id={describedBy('fuelOptionId')}>{errors.fuelOptionId}</FieldError>
          {scan.ocr.fuelText && (
            <p className="mt-1 text-[11px] text-on-surface-variant">
              OCR read: <span className="font-data-mono">{scan.ocr.fuelText}</span>
            </p>
          )}
          {ocrHint('fuelOptionId', scan.ocr.fuelOption?.name ?? '')}
        </div>

        <div>
          <Label htmlFor={fieldId('unitPrice')} extra={mathText}>
            Unit Price (€ / {unit})
          </Label>
          {editable ? (
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant text-sm" aria-hidden="true">
                €
              </span>
              <input type="number" inputMode="decimal" step="0.001" min="0" max="100" {...common('unitPrice')} className={input('unitPrice', 'pl-8')} />
            </div>
          ) : (
            <ReadValue id={fieldId('unitPrice')}>{formatPrice(scan.unitPrice)}</ReadValue>
          )}
          <FieldError id={describedBy('unitPrice')}>{errors.unitPrice}</FieldError>
          {ocrHint('unitPrice', formatPrice(scan.ocr.unitPrice))}
        </div>

        <div>
          <Label htmlFor={fieldId('volume')}>Fuel Volume ({unit === 'L' ? 'Liters' : unit})</Label>
          {editable ? (
            <input type="number" inputMode="decimal" step="0.01" min="0" max="500" {...common('volume')} className={input('volume')} />
          ) : (
            <ReadValue id={fieldId('volume')}>{formatVolume(scan.volume, unit)}</ReadValue>
          )}
          <FieldError id={describedBy('volume')}>{errors.volume}</FieldError>
          {ocrHint('volume', formatVolume(scan.ocr.volume, unit))}
        </div>

        <div>
          <Label htmlFor={fieldId('totalAmount')}>Total Amount (€)</Label>
          {editable ? (
            <input type="number" inputMode="decimal" step="0.01" min="0" max="10000" {...common('totalAmount')} className={input('totalAmount')} />
          ) : (
            <ReadValue id={fieldId('totalAmount')}>{formatAmount(scan.totalAmount)}</ReadValue>
          )}
          <FieldError id={describedBy('totalAmount')}>{errors.totalAmount}</FieldError>
          {ocrHint('totalAmount', formatAmount(scan.ocr.totalAmount))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-5">
        <div>
          <Label htmlFor={fieldId('receiptAt')}>Receipt Timestamp</Label>
          {editable ? (
            <input type="datetime-local" step="1" {...common('receiptAt')} className={input('receiptAt', 'text-[13px]')} />
          ) : (
            <ReadValue id={fieldId('receiptAt')}>{formatAmsterdam(scan.receiptAt)}</ReadValue>
          )}
          <FieldError id={describedBy('receiptAt')}>{errors.receiptAt}</FieldError>
          {ocrHint('receiptAt', formatAmsterdam(scan.ocr.receiptAt))}
          <p className={`mt-1 flex items-center gap-1 text-[11px] ${typeof age === 'number' && age > 7 ? 'text-error font-semibold' : 'text-on-surface-variant'}`}>
            <Icon name="schedule" size={14} />
            Receipt Age: {typeof age === 'number' ? `${age} ${age === 1 ? 'day' : 'days'}` : '—'}
          </p>
          <p className="mt-0.5 text-[10px] text-on-surface-variant">Netherlands time (Europe/Amsterdam)</p>
        </div>

        <div>
          <Label htmlFor={fieldId('receiptNumber')}>Ticket Number</Label>
          {editable ? (
            <input
              type="text"
              autoComplete="off"
              spellCheck={false}
              maxLength={40}
              {...common('receiptNumber')}
              onChange={(e) => onChange('receiptNumber', normalizeReceiptNumber(e.target.value))}
              className={input('receiptNumber', 'uppercase')}
            />
          ) : (
            <ReadValue id={fieldId('receiptNumber')}>{scan.receiptNumber || '—'}</ReadValue>
          )}
          <FieldError id={describedBy('receiptNumber')}>{errors.receiptNumber}</FieldError>
          {ocrHint('receiptNumber', ocrFormValue(scan, 'receiptNumber') ?? '')}
        </div>

        <div>
          <span className="block text-xs font-semibold text-on-surface mb-1.5">Pump ID</span>
          <p title="Read from the receipt text" className="h-11 px-3 flex items-center rounded-lg bg-surface-container-low text-sm font-data-mono text-on-surface cursor-help">
            {scan.pumpNumber ?? '—'}
          </p>
        </div>
      </div>
    </section>
  );
}
